/**
 * Runtime v3 — THE TYPED ANALYTICAL REQUEST (#8051, increment 13 PR A).
 *
 * The model's job is to say what the question MEANS. This type is the whole of
 * what it may say, and it reuses the read contracts rather than restating
 * them: the tenant, the period grain, the dimension and member vocabulary and
 * the budget all belong to `query-spec.ts` and `dataset-revision.ts`, and a
 * second spelling of any of them would be a second thing to keep in step.
 *
 * WHAT IS ACTUALLY NEW HERE, and nothing else is:
 *
 *   - the ANALYTICAL OPERATION, with its operand and output references, so a
 *     request is a small typed graph rather than a sentence;
 *   - the RESULT CLASS an operand asks for (actual, budget, modeled, run rate)
 *     — an amount is not a number until you know which of those it is;
 *   - the SEMANTIC FAMILY an operand names, which is resolved to a dataset and
 *     a measure by DECLARED PROVIDER RELATIONSHIPS (`provider-relationship.ts`)
 *     and never by which alias happened to match;
 *   - ORIGIN MARKERS (`requested` / `defaulted`) on the interpretation and
 *     default decisions the model made, so an exhibit can say "you asked for"
 *     rather than "we assumed".
 *
 * WHAT IS DELIBERATELY ABSENT. There is no resolved dataset, no revision, no
 * resolved window and no member ref: those are SERVER-AUTHORED outcomes and
 * they live in `analytical-resolution.ts`. Keeping them out of this type is
 * what makes "the model resolved meaning, the server resolved providers"
 * checkable rather than merely intended — a request that carried a datasetRef
 * would be a request that had already chosen.
 */

import type { IsoInstant, RuntimeV3SchemaVersion } from './common';
import type { RuntimeV3QueryPeriod, RuntimeV3QuerySpec } from './query-spec';
import type { RuntimeV3ProviderBasis } from './provider-relationship';

/** Did the request say this, or did something choose it on the reader's behalf? */
export const RUNTIME_V3_DECISION_ORIGINS = ['requested', 'defaulted'] as const;
export type RuntimeV3DecisionOrigin = (typeof RUNTIME_V3_DECISION_ORIGINS)[number];

/** One decided value, carrying how it came to be decided. */
export type RuntimeV3Decided<T> = Readonly<{ value: T; origin: RuntimeV3DecisionOrigin }>;

/**
 * WHAT KIND OF NUMBER IS BEING ASKED FOR.
 *
 * Two figures of the same family at the same grain over the same window are
 * still different answers when one is an actual and the other a run rate, so
 * the class is part of the request rather than a note beside the answer.
 *
 * DELIBERATELY NOT `RuntimeV3ResultClass` (`result-value-policy.ts`). That one
 * says how the runtime PRODUCED a figure — read off stored facts, computed by
 * a capability graph, or refused — and it is server-authored. This one says
 * what the reader ASKED FOR. One vocabulary for both would make "the reader
 * wanted budget" and "the engine read a fact" the same field, which is exactly
 * the confusion the request/resolution split exists to prevent.
 */
export const RUNTIME_V3_REQUESTED_RESULT_CLASSES = ['actual', 'budget', 'modeled', 'run_rate'] as const;
export type RuntimeV3RequestedResultClass = (typeof RUNTIME_V3_REQUESTED_RESULT_CLASSES)[number];

/**
 * The operations PR A resolves end to end.
 *
 * `total` is one figure for the operand; `by_member` is one figure per member
 * of the named dimensions. Ratio, share, comparison and scenario are typed
 * multi-output composition and are PR B's — a request naming one is refused
 * `unsupported_calculation` here rather than approximated.
 */
export const RUNTIME_V3_ANALYTICAL_OPERATIONS = [
  'total', 'by_member', 'series', 'count', 'rank', 'subtract',
  'share_of_total', 'period_over_period', 'scenario_estimate',
  'traverse',
  'biggest_mover', 'gap_to_target', 'on_track',
] as const;
export type RuntimeV3AnalyticalOperation = (typeof RUNTIME_V3_ANALYTICAL_OPERATIONS)[number];

export const RUNTIME_V3_RANK_LIMIT = Object.freeze({ min: 1, max: 25 });
/**
 * `n` IS ABSENT WHEN THE QUESTION STATED NO COUNT (desk ruling 2026-09-17,
 * #8375 review; I15 stands).
 *
 * A superlative alone never licenses N = 1, and the server does not mint a
 * count of its own either: a question with a plural subject and no number is
 * asking for the RANKED LIST, so an omitted `n` means every member that exists
 * for the grouping, ordered in the stated direction under the tie policy. It
 * is typed as absent rather than defaulted, because a default here is a
 * business number nobody asked for, and the grouping's own budget already
 * bounds how many members a query may build. A STATED `n` is unchanged: it
 * still has to clear the digit rule and the registered rank limit.
 */
export type RuntimeV3AnalyticalRank = Readonly<{
  direction: 'asc' | 'desc';
  n?: number;
  /** Equal measures are ordered by the pinned member label ascending. */
  tiePolicy: 'label';
}>;

/**
 * The period AS THE REQUEST LEFT IT — named, or not named.
 *
 * `explicit_range` is a window the reader actually stated and is never
 * overridden (fold 82). `omitted` is the honest form for a question that named
 * no window at all: it is resolved on the server to the latest published
 * period and the answer says it was defaulted. There is no third form here,
 * because a relative window the MODEL computed is a number the model invented
 * — relative forms are declared in `query-spec.ts` and resolved against the
 * server's own `asOf`.
 *
 * DERIVED FROM `RuntimeV3QueryPeriod`, NOT RETYPED (Astra amendment 1). The
 * grain, start and end are that type's own members, picked out here; the only
 * thing this adds is which of them each form REQUIRES, and the deliberate
 * absence of `asOf`, which is the server's to stamp and never the request's to
 * state. A second spelling of `IsoDate` or of the grain vocabulary would be a
 * second thing to keep in step.
 */
export type RuntimeV3RequestedPeriod =
  | (Readonly<{ form: 'last_n' }> & Required<Pick<RuntimeV3QueryPeriod, 'grain' | 'n'>>)
  | (Readonly<{ form: 'explicit_range' }>
    & Pick<RuntimeV3QueryPeriod, 'grain'>
    & Required<Pick<RuntimeV3QueryPeriod, 'start' | 'end'>>)
  | (Readonly<{ form: 'omitted' }> & Pick<RuntimeV3QueryPeriod, 'grain'>)
  /** Every period the selected provider publishes; bound by the server, never dated here. */
  | (Readonly<{ form: 'all_coverage' }> & Pick<RuntimeV3QueryPeriod, 'grain'>);

/**
 * WHAT IS BEING MEASURED, semantically.
 *
 * `familyRef` names a semantic metric or population — "the SG&A spend total",
 * "the governed lever posture" — not a dataset measure. Resolution to a
 * dataset and a measure is the server's, through declared relationships.
 *
 * `basis` is present only when the question itself distinguished one (or a
 * declared default supplied it), and it carries its origin for exactly that
 * reason: the difference between a reader who said "on the GL basis" and a
 * runtime that decided for them is the whole of what a clarification exists to
 * protect.
 */
export type RuntimeV3AnalyticalOperand = Readonly<{
  operandId: string;
  familyRef: string;
  resultClass: RuntimeV3RequestedResultClass;
  basis?: RuntimeV3Decided<RuntimeV3ProviderBasis>;
  /**
   * The dimensions whose members the request's OWN WORDING should be resolved
   * against, inside the chosen dataset. Naming a dimension is not naming a
   * member: the member comes from the request text through discovery, with a
   * receipt, or it does not come at all.
   */
  memberDimensionRefs: readonly string[];
}>;

/**
 * One thing the request wants back, over one operand.
 *
 * `groupByDimensionRefs` is `RuntimeV3QuerySpec`'s own `groupBy` (Astra
 * amendment 1), not a second spelling of it. Which operation requires it is
 * ENFORCED, not merely documented: `analyticalRequestDefects` refuses `total`
 * that carries grouping and `by_member` that carries none (D8), so the two
 * words in the enum mean two different plans rather than one plan with a
 * decorative label.
 */
export type RuntimeV3AnalyticalLexicalMemberFilter = Readonly<{
  kind?: 'lexical';
  dimensionRef: string;
  memberRef: string;
  phrase: string;
}>;

/** Server-minted context required to spend an opaque M1 member-selection handle. */
export type RuntimeV3AnalyticalSemanticMemberFilter = Readonly<{
  kind: 'semantic_selection';
  selectionId: string;
  dimensionRef: string;
  memberRef: string;
  phrase: string;
  sourceSpan: string;
  verification: Readonly<{
    /**
     * `governed_view`: minted only by a governed view for members it already holds (no reader
     * span), bound to that view's own turn (#9302).
     */
    authorization: 'direct_request' | 'continuation_projection' | 'governed_view';
    projectionDigest: string;
    tenantId: string;
    actorId: string;
    conversationId: string;
    turnIndex: number;
    continuationAnswerId?: string;
    outputId: string;
    filterIndex: number;
  }>;
}>;

export type RuntimeV3AnalyticalMemberFilter =
  | RuntimeV3AnalyticalLexicalMemberFilter
  | RuntimeV3AnalyticalSemanticMemberFilter;

export type RuntimeV3AnalyticalSourceOutput = Readonly<{
  outputId: string;
  operation: RuntimeV3AnalyticalOperation;
  operandId: string;
  period?: RuntimeV3RequestedPeriod;
  groupByDimensionRefs: RuntimeV3QuerySpec['groupBy'];
  rank?: RuntimeV3AnalyticalRank;
  /** Server-resolved identities, verified again against the execution revision. */
  memberFilters?: readonly RuntimeV3AnalyticalMemberFilter[];
}>;

/**
 * A governed derivation consumes prior outputs, never a semantic operand.
 *
 * `operandRoles` is #8351 PR 1's alternate path: instead of naming other
 * DECLARED outputs, the model maps every role the operation's registry entry
 * declares (`requires`) straight to an operand id, and the compiler mints the
 * reads itself (`analytical-plan-expansion.ts`, apps/api). The two paths are
 * mutually exclusive in practice — an output using `operandRoles` carries an
 * EMPTY `inputOutputIds`, so every reader that already tests
 * `'inputOutputIds' in output` for "is this derived" keeps working unchanged.
 */
export type RuntimeV3AnalyticalDerivedOutput = Readonly<{
  outputId: string;
  operation: RuntimeV3AnalyticalOperation;
  inputOutputIds: readonly string[];
  operandRoles?: Readonly<Record<string, string>>;
}>;

/**
 * #8351 HOLD cure AR-01/finding 2 — the id space the compiler mints
 * synthesized internal source nodes into (`analytical-plan-expansion.ts`,
 * apps/api). No output the model or an author names may use it: a public
 * output naming this namespace is refused as `reserved_output_id`
 * (`analytical-request-validation.ts`) before provider selection ever runs,
 * so a collision with a compiler-minted id is structurally impossible.
 */
export const RUNTIME_V3_INTERNAL_OUTPUT_ID_PREFIX = 'internal:';

export type RuntimeV3AnalyticalOutput =
  | RuntimeV3AnalyticalSourceOutput
  | RuntimeV3AnalyticalDerivedOutput;

export function runtimeV3AnalyticalOutputIsDerived(
  output: RuntimeV3AnalyticalOutput,
): output is RuntimeV3AnalyticalDerivedOutput {
  return 'inputOutputIds' in output;
}

/**
 * THE REQUEST.
 *
 * IT CARRIES NO TENANT, AND THAT IS A SECURITY PROPERTY, NOT A CONVENIENCE
 * (D1). The request is MODEL-AUTHORED; a tenant id inside it would be
 * authority the model could state, and every read keyed on it — the provider
 * registry, the member catalog, the resolution handed back — would be a read
 * a request could aim at another tenant. The scope binds from authorization
 * (Astra amendment 1): the server takes the tenant from the authenticated
 * actor and from nowhere else, so a foreign tenant is not refused late, it is
 * unsayable.
 *
 * `asOf` is the instant every relative resolution is taken against, carried
 * with its origin because a question that says "as of June" and a turn that
 * took the server clock are different claims about the same number.
 *
 * `requestText` is the reader's own words. It is carried so discovery can
 * resolve members from spans of it and so a receipt can be checked against it.
 * Nothing in this runtime branches on it.
 */
export type RuntimeV3AnalyticalRequest = Readonly<{
  schemaVersion: RuntimeV3SchemaVersion;
  requestText: string;
  asOf: RuntimeV3Decided<IsoInstant>;
  period: RuntimeV3RequestedPeriod;
  operands: readonly RuntimeV3AnalyticalOperand[];
  outputs: readonly RuntimeV3AnalyticalOutput[];
}>;

/**
 * THE REQUEST'S MEANING, WITHOUT ITS WORDING.
 *
 * Two runs of the same question and one run of a paraphrase must produce the
 * same canonical request: the accepted interpretation is what determinism is
 * about, and the sentence it arrived in is not part of it. `requestText` is
 * therefore excluded — it is free text — while every reference, class,
 * operation and origin marker is kept, because changing any of them changes
 * what was asked.
 *
 * THE AS-OF IS NOT ERASED (Astra amendment 4). An earlier form kept only its
 * ORIGIN, on the reasoning that a clock reading is not part of meaning. That
 * is wrong for a runtime whose windows, effective-period tests and default
 * periods are all taken against it: two requests differing only in as-of are
 * two different questions, and a canonical form that could not tell them apart
 * would let a replay at another moment compare equal to one it is not.
 */
/**
 * Takes the CHECKED request. A caller holding an unchecked body canonicalizes
 * it only after `analyticalRequestDefects` has come back empty — a malformed
 * request has no accepted meaning to serialize, and the resolution that
 * carries its defects is what a reader gets instead.
 */
export function canonicalAnalyticalRequest(
  candidate: RuntimeV3AnalyticalRequest | unknown,
): Readonly<Record<string, unknown>> {
  const request = candidate as RuntimeV3AnalyticalRequest;
  return {
    schemaVersion: request.schemaVersion,
    asOf: { value: request.asOf.value, origin: request.asOf.origin },
    period: request.period,
    operands: request.operands.map((operand) => ({
      operandId: operand.operandId,
      familyRef: operand.familyRef,
      resultClass: operand.resultClass,
      ...(operand.basis === undefined ? {} : { basis: operand.basis }),
      memberDimensionRefs: [...operand.memberDimensionRefs],
    })),
    outputs: request.outputs.map((output) => (runtimeV3AnalyticalOutputIsDerived(output)
      ? {
        outputId: output.outputId,
        operation: output.operation,
        inputOutputIds: [...output.inputOutputIds],
        ...(output.operandRoles === undefined ? {} : { operandRoles: { ...output.operandRoles } }),
      }
      : {
        outputId: output.outputId,
        operation: output.operation,
        operandId: output.operandId,
        ...(output.period === undefined ? {} : { period: output.period }),
        groupByDimensionRefs: [...output.groupByDimensionRefs],
        ...(output.rank === undefined ? {} : { rank: { ...output.rank } }),
        ...(output.memberFilters === undefined ? {} : {
          memberFilters: output.memberFilters.map(({ dimensionRef, memberRef }) => ({
            dimensionRef, memberRef,
          })),
        }),
      })),
  };
}

// I14-00 shared exports through the existing analytical-request barrel edge.
export * from './operation-registry';
